import { Button, buttonCls } from '@qr-front/ui/components/Button'
import { cn } from '@qr-front/ui/utils'
import {
	businessInfoButtonListAtom,
	businessInfoCompanyNameAtom,
	businessInfoSubtitleAtom,
	businessInfoTitleAtom,
} from 'features/generator/contentFormSections/BusinessInfoForm'
import { TBusinessButton } from 'features/generator/contentFormSections/BusinessInfoForm/businessInfoFormModel'
import {
	imageFormAtom,
	imageFormFileAtom,
} from 'features/generator/contentFormSections/ImageForm'
import { TQrScreenProps } from 'features/generator/QrScreen/QrScreen'
import { useFrameFontSize } from 'features/generator/services/client'
import { useAtomValue } from 'jotai'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useState } from 'react'
import { addMissingUrlProtocol } from 'utils/urlUtils'

import { useColoredButton } from '../../hooks/useColoredButton'
import {
	businessPagePrimaryColorAtom,
	businessPageSecondaryColorAtom,
} from './BusinessPageScreenState'

export const BusinessPageHeader: React.FC<TQrScreenProps> = ({
	previewMode,
	demoMode,
}) => {
	const t = useTranslations()

	const primaryColor = useAtomValue(businessPagePrimaryColorAtom)
	const secondaryColor = useAtomValue(businessPageSecondaryColorAtom)
	const { coloredButtonCls, coloredButtonStyle } =
		useColoredButton(secondaryColor)

	const buttonList = useAtomValue(businessInfoButtonListAtom)

	const [imageHeight, setImageHeight] = useState(96) // default height from min-h-24 (96px)
	const companyImage = useAtomValue(imageFormAtom)
	const companyImageFile = useAtomValue(imageFormFileAtom)

	const [localPreviewSrc, setLocalPreviewSrc] = useState<string | null>(null)
	useEffect(() => {
		if (!companyImageFile) {
			setLocalPreviewSrc(null)
			return
		}
		const url = URL.createObjectURL(companyImageFile)
		setLocalPreviewSrc(url)
		return () => URL.revokeObjectURL(url)
	}, [companyImageFile])

	const imageSrc = companyImage?.publicId
		? `${companyImage.bucketRootUrl}/${companyImage.publicId}`
		: localPreviewSrc

	const handleImageLoad = useCallback(
		(event: React.SyntheticEvent<HTMLImageElement>) => {
			const img = event.currentTarget
			setImageHeight(img.offsetHeight)
		},
		[],
	)
	const companyName = useAtomValue(businessInfoCompanyNameAtom)
	const displayedCompanyName = demoMode
		? t(
				'generator__qr_content_screen__business_page_type__sections__company_name',
			)
		: companyName
	const companyTitle = useAtomValue(businessInfoTitleAtom)
	const displayedCompanyTitle = demoMode
		? t(
				'generator__qr_content_screen__business_page_type__sections__company_title',
			)
		: companyTitle
	const companySubtitle = useAtomValue(businessInfoSubtitleAtom)
	const displayedCompanySubtitle = demoMode
		? t(
				'generator__qr_content_screen__business_page_type__sections__company_subtitle',
			)
		: companySubtitle

	const hasImage = demoMode || Boolean(imageSrc)
	const backgroundHeight = hasImage ? 32 + imageHeight / 2 : 32

	return (
		<>
			<div className="relative">
				<div
					className={cn('h-[4.9rem] px-5 transition-colors')}
					style={{ backgroundColor: primaryColor }}
				></div>
				<div
					className="absolute top-0 w-full transition-colors"
					style={{
						backgroundColor: primaryColor,
						height: `${backgroundHeight}px`,
					}}
				/>
			</div>
			<div
				className={cn(
					'relative z-10 flex flex-col items-center',
					hasImage && '-mt-14',
				)}
			>
				{hasImage && (
					<div className="min-h-25 w-[220px] overflow-hidden rounded-4 border-2 border-white bg-white drop-shadow-generator">
						{demoMode && !imageSrc ? (
							<Image
								src="/web-app/qr-preview-demos/business-page/business-page-picture.webp"
								alt="Company image"
								width={216}
								height={96}
								className="h-full min-h-24 w-full rounded-4 object-cover"
								onLoad={handleImageLoad}
							/>
						) : null}
						{imageSrc ? (
							// eslint-disable-next-line @next/next/no-img-element
							<img
								src={imageSrc}
								alt="Company image"
								width={216}
								className="h-full min-h-24 w-full rounded-4 object-cover"
								onLoad={handleImageLoad}
							/>
						) : null}
					</div>
				)}
				<div className="w-full max-w-qr-screen-constrained px-5 py-4">
					<div className="flex flex-col gap-4">
						<div className="flex flex-col gap-2 word-break-fix">
							<div>
								<h4 className="type-heading-4 text-center transition-colors">
									{displayedCompanyName}
								</h4>
								<p className="text-center text-10 text-dark-grey">
									{displayedCompanyTitle}
								</p>
							</div>
							<p className="text-center font-body text-10 leading-[0.95rem]">
								{displayedCompanySubtitle}
							</p>
						</div>
						{demoMode ? (
							<Button
								visual="secondary"
								className={cn(
									coloredButtonCls,
									'w-full text-body-caption',
									'rounded-6',
								)}
								style={coloredButtonStyle}
							>
								{t(
									'generator__qr_content_screen__business_page_type__sections__learn_more__button',
								)}
							</Button>
						) : null}
						{buttonList.map((button, index) => (
							<CustomButton
								key={index}
								button={button}
								previewMode={previewMode}
								demoMode={demoMode}
							/>
						))}
					</div>
				</div>
			</div>
			<div className="h-6" />
		</>
	)
}

const CustomButton = ({
	button: { title, url },
	previewMode,
	demoMode,
}: {
	button: TBusinessButton
	previewMode: boolean
	demoMode: boolean
}) => {
	const t = useTranslations()
	const secondaryColor = useAtomValue(businessPageSecondaryColorAtom)

	const { coloredButtonCls, coloredButtonStyle } =
		useColoredButton(secondaryColor)

	const displayedTitle = demoMode
		? t(
				'generator__qr_content_screen__business_page_type__sections__learn_more__button',
			)
		: title

	const fontSize = useFrameFontSize({
		text: title,
		maxWidth: 220,
		maxSize: previewMode || demoMode ? 12 : 15,
		fontFamily: 'var(--font-body)',
	})

	const style = {
		fontSize: `${fontSize}px`,
		...coloredButtonStyle,
	}

	const baseButtonCls = cn(
		buttonCls({ visual: 'secondary' }),
		'w-full grow text-body-caption font-normal',
	)

	if (!url) {
		return (
			<span className={cn(baseButtonCls, 'cursor-not-allowed')} style={style}>
				{displayedTitle}
			</span>
		)
	}

	return (
		<a
			href={addMissingUrlProtocol(url)}
			className={cn(baseButtonCls, coloredButtonCls)}
			target="_blank"
			style={style}
			rel="noreferrer"
		>
			{displayedTitle}
		</a>
	)
}
